import { EMPLOYEE_CONTRACT_TYPES } from 'domain/shared/employee';

export type EmployeeAssignmentContext = {
  officeId?: Nullable<string | number>;
  positionId?: Nullable<string | number>;
  contractType?: Nullable<string>;
  businessRegistrationCountry?: Nullable<string>;
  residencyRegion?: Nullable<string>;
  residencyCountry?: Nullable<string>;
  account?: Nullable<string>;
  project?: Nullable<string>;
  projectId?: Nullable<string | number>;
  projectIds?: string[];
};

type FilterValue<T> = {
  in?: T[];
};

export type RelatedEmployeeFilter = Nullable<{
  [K in keyof EmployeeAssignmentContext]?: FilterValue<NonNullable<EmployeeAssignmentContext[K]>>;
}>;

export type AssignmentReasonDictionaries = {
  officesById: Record<string, string>;
  positionsById: Record<string, string>;
  contractTypesById: Record<string, string>;
  accountsById: Record<string, string>;
  projectsById: Record<string, string>;
  projectIdsByAccountId: Record<string, string[]>;
};

type FilterKey = keyof NonNullable<RelatedEmployeeFilter>;

const ASSIGNMENT_REASON_CONFIG: Array<{
  key: FilterKey;
  buildReasonPart: (value: string) => string;
  dictionaryKey?: Exclude<keyof AssignmentReasonDictionaries, 'projectIdsByAccountId'>;
  employeeValueKey?: keyof EmployeeAssignmentContext;
}> = [
  {
    key: 'positionId',
    buildReasonPart: value => `your position is ${value}`,
    dictionaryKey: 'positionsById',
    employeeValueKey: 'positionId',
  },
  {
    key: 'officeId',
    buildReasonPart: value => `your office is ${value}`,
    dictionaryKey: 'officesById',
    employeeValueKey: 'officeId',
  },
  {
    key: 'residencyRegion',
    buildReasonPart: value => `your residency region is ${value}`,
    employeeValueKey: 'residencyRegion',
  },
  {
    key: 'residencyCountry',
    buildReasonPart: value => `your residency country is ${value}`,
    employeeValueKey: 'residencyCountry',
  },
  {
    key: 'businessRegistrationCountry',
    buildReasonPart: value => `your business is registered in ${value}`,
    employeeValueKey: 'businessRegistrationCountry',
  },
  {
    key: 'contractType',
    buildReasonPart: value => `your contract type is ${value}`,
    dictionaryKey: 'contractTypesById',
    employeeValueKey: 'contractType',
  },
  {
    key: 'project',
    buildReasonPart: value => `your project is ${value}`,
    dictionaryKey: 'projectsById',
    employeeValueKey: 'projectId',
  },
];

export const contractTypesById = EMPLOYEE_CONTRACT_TYPES;

const mapValue = (value: unknown, dictionary: Record<string, string> = {}) =>
  dictionary[String(value)] || String(value);

const createReasonPart = (value: string | null | undefined, buildReasonPart: (value: string) => string) => {
  if (!value) {
    return null;
  }

  return buildReasonPart(value);
};

const getUniqueStringValues = (values: Array<Nullable<string>>) =>
  Array.from(new Set(values.filter(Boolean).map(String)));

const findMatchedValue = (
  values: unknown[],
  employeeValue: EmployeeAssignmentContext[keyof EmployeeAssignmentContext],
  dictionary: Record<string, string>,
) => {
  if (employeeValue !== undefined && employeeValue !== null) {
    const matchedValue = values.find(value => String(value) === String(employeeValue));

    if (matchedValue !== undefined) {
      return mapValue(matchedValue, dictionary);
    }
  }

  return mapValue(values[0], dictionary);
};

const getAssignmentReasonParts = (
  filter: RelatedEmployeeFilter,
  relatedEmployeeProjectIds: Nullable<string[]>,
  relatedEmployeeAccountIds: Nullable<string[]>,
  dictionaries: AssignmentReasonDictionaries,
  employeeContext: EmployeeAssignmentContext,
): string[] => {
  if (!filter && !relatedEmployeeProjectIds?.length && !relatedEmployeeAccountIds?.length) {
    return ['Applies to all employees'];
  }

  const filterReasonParts = ASSIGNMENT_REASON_CONFIG.reduce<string[]>(
    (result, { key, buildReasonPart, dictionaryKey, employeeValueKey }) => {
      const values = filter?.[key]?.in;

      if (!values?.length) return result;

      const dictionary = dictionaryKey ? dictionaries[dictionaryKey] : {};
      const employeeValue = employeeValueKey ? employeeContext[employeeValueKey] : null;
      const reasonPart = createReasonPart(findMatchedValue(values, employeeValue, dictionary), buildReasonPart);

      if (reasonPart) result.push(reasonPart);

      return result;
    },
    [],
  );

  const toUniqueIds = (values: Array<Nullable<string>>) => getUniqueStringValues(values);
  const toProjectNames = (projectIds: string[]) =>
    getUniqueStringValues(projectIds.map(projectId => dictionaries.projectsById[String(projectId)]));

  const accountIds = toUniqueIds([...(filter?.account?.in || []), ...(relatedEmployeeAccountIds || [])]);
  const employeeProjectIds = toUniqueIds([
    employeeContext.projectId ? String(employeeContext.projectId) : null,
    ...(employeeContext.projectIds || []),
  ]);

  const accountProjectIds = toUniqueIds(
    accountIds
      .flatMap(accountId => dictionaries.projectIdsByAccountId[String(accountId)] || [])
      .filter(projectId => employeeProjectIds.includes(String(projectId))),
  );

  const directProjectIds = toUniqueIds((relatedEmployeeProjectIds || []).map(String)).filter(projectId =>
    employeeProjectIds.includes(projectId),
  );
  const mergedProjectNames = getUniqueStringValues([
    ...toProjectNames(accountProjectIds),
    ...toProjectNames(directProjectIds),
  ]);
  const directReasonParts = [
    createReasonPart(mergedProjectNames.join(', '), value => `your project is ${value}`),
  ].filter(Boolean) as string[];

  return [...filterReasonParts, ...directReasonParts];
};

const formatAssignmentReasons = (reasonParts: string[]) => {
  if (!reasonParts.length) {
    return '';
  }

  if (reasonParts[0] === 'Applies to all employees') {
    return reasonParts[0];
  }

  const [firstReason, ...otherReasons] = reasonParts;

  return `Applies because ${firstReason}${otherReasons.length ? ` and ${otherReasons.join(' and ')}` : ''}.`;
};

export const getAssignmentReasonTitle = (
  filter: RelatedEmployeeFilter,
  relatedEmployeeProjectIds: Nullable<string[]>,
  relatedEmployeeAccountIds: Nullable<string[]>,
  dictionaries: AssignmentReasonDictionaries,
  employeeContext: EmployeeAssignmentContext,
) =>
  formatAssignmentReasons(
    getAssignmentReasonParts(
      filter,
      relatedEmployeeProjectIds,
      relatedEmployeeAccountIds,
      dictionaries,
      employeeContext,
    ),
  );
